import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SendIcon from "@mui/icons-material/Send";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormHelperText from "@mui/material/FormHelperText";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import LoadingButton from "_shared/_views/loading/loading-button";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { useState } from "react";
import type { ProposalFirmSendResultJSON } from "work/proposals/bidding/_api/proposal-firm-send-api";
import ProposalFirmPicker, { type ChosenFirm, FirmPickerKind } from "work/proposals/bidding/_views/proposal-firm-picker";
import {
  isSendableFirmCount,
  MAXIMUM_FIRMS_PER_SEND,
  MINIMUM_FIRMS_PER_SEND,
  type ProposalFirm,
  reviewProposalFirms,
} from "work/proposals/bidding/proposal-firm";

const FirmChips = styled("ul")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  listStyle: "none",
  margin: theme.spacing(1, 0),
  padding: 0,
}));

const Explanation = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
}));

/**
 * The request a send's proposals answer, when at least one firm received its proposal. The client
 * compares the proposals that went out by it (#6457); a send no firm received has nothing to compare.
 */
export function sentRequestId(results: readonly ProposalFirmSendResultJSON[]): string | undefined {
  return results.find((result) => result.outcome === "Sent")?.rfpId;
}

/** Whether every firm received its proposal, so there is nothing for the client to read before comparing. */
export function everyFirmReceivedIt(results: readonly ProposalFirmSendResultJSON[]): boolean {
  return results.length > 0 && results.every((result) => result.outcome === "Sent");
}

/**
 * A firm chosen as a marketplace team is addressed to the whole team -- its members and the
 * marketplace team it came from -- as the draft's vendor team tab addresses one (#576 review).
 */
export function proposalFirmFromChoice(chosen: ChosenFirm): ProposalFirm {
  return {
    leader: chosen.member,
    name: chosen.name,
    members: chosen.team?.members,
    marketplaceId: chosen.team?.marketplaceId,
  };
}

enum SendStep {
  Choose = "choose",
  Review = "review",
  Results = "results",
}

export type SendToSeveralFirmsDialogProps = {
  /** The firm the draft already names, if it names one: the list starts with it. */
  initialFirms: readonly ProposalFirm[];
  clientLegalEntityId?: Guid;
  /** The one response deadline every firm's copy carries. */
  responseDueBy?: AHDate | null;
  onSend: (firms: readonly ProposalFirm[]) => Promise<ProposalFirmSendResultJSON[]>;
  /**
   * Closes the dialog. `sentRequestId` is the request the sent proposals answer, when at least one
   * firm received the proposal; the client goes on to compare them by it. Undefined when none did.
   */
  onClose: (sentRequestId: string | undefined) => void;
};

/**
 * Sends one proposal to several firms to bid on the same work (#576).
 *
 * The client chooses the firms, reviews the list -- each firm and whether it can receive the
 * proposal -- and sends identical copies in one action. Each firm sees only its own proposal. When
 * every firm received it, the dialog closes straight onto the comparison of the send (#6457) rather
 * than offering a link per firm. When a firm did not, the dialog says which and why first -- the
 * others are still sent -- and then goes on to the comparison.
 *
 * There is no per-firm edit here on purpose: every firm receives the same terms and the same
 * response deadline. To change the terms for one firm, the client redlines its proposal afterwards.
 */
export default function SendToSeveralFirmsDialog(props: Readonly<SendToSeveralFirmsDialogProps>) {
  const { initialFirms, clientLegalEntityId, responseDueBy, onSend, onClose } = props;
  const [step, setStep] = useState<SendStep>(SendStep.Choose);
  const [firms, setFirms] = useState<ProposalFirm[]>(() => [...initialFirms]);
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement>();
  const [pickerKind, setPickerKind] = useState<FirmPickerKind>();
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string>();
  const [results, setResults] = useState<ProposalFirmSendResultJSON[]>([]);

  const readiness = reviewProposalFirms(firms, clientLegalEntityId);
  const readyCount = readiness.filter((entry) => entry.canReceive).length;
  const isAtLimit = firms.length >= MAXIMUM_FIRMS_PER_SEND;
  const requestId = sentRequestId(results);

  function addFirm(chosen: ChosenFirm) {
    setPickerKind(undefined);
    setFirms((current) =>
      current.some((firm) => firm.leader.isSamePersonAs(chosen.member))
        ? current
        : [...current, proposalFirmFromChoice(chosen)],
    );
  }

  function removeFirm(index: number) {
    setFirms((current) => current.filter((_, position) => position !== index));
  }

  // No `finally`: the React Compiler cannot lower one, and would leave this component unoptimized.
  async function handleSend() {
    setIsSending(true);
    setSendError(undefined);
    let answered: ProposalFirmSendResultJSON[] | undefined;
    try {
      answered = await onSend(firms);
    } catch (error) {
      setSendError(getErrorMessageFromUnknown(error) ?? "The proposal could not be sent. Try again.");
    }
    setIsSending(false);
    if (!answered) return;

    if (everyFirmReceivedIt(answered)) {
      onClose(sentRequestId(answered));
      return;
    }
    setResults(answered);
    setStep(SendStep.Results);
  }

  function resultsTitle() {
    return requestId ? "Some firms did not receive the proposal" : "The proposal was not sent";
  }

  return (
    <>
      <Dialog open onClose={() => !isSending && onClose(requestId)} fullWidth maxWidth="sm" aria-labelledby="send-to-firms-title">
        <DialogTitle id="send-to-firms-title">
          {step === SendStep.Results ? resultsTitle() : "Send to several firms"}
        </DialogTitle>
        <DialogContent>
          {step === SendStep.Choose && (
            <>
              <Explanation variant="body2">
                Each firm receives its own copy of this proposal and negotiates with you on it. A firm sees only its
                own proposal.
              </Explanation>
              <FirmChips aria-label="Firms">
                {firms.map((firm, index) => (
                  <li key={`${firm.leader.userId.value}-${index}`}>
                    <Chip label={firm.name} onDelete={() => removeFirm(index)} />
                  </li>
                ))}
              </FirmChips>
              <Button
                size="small"
                startIcon={<AddIcon />}
                endIcon={<ArrowDropDownIcon />}
                disabled={isAtLimit}
                aria-haspopup="menu"
                onClick={(event) => setAddMenuAnchor(event.currentTarget)}
              >
                Add firm
              </Button>
              <Menu anchorEl={addMenuAnchor} open={addMenuAnchor !== undefined} onClose={() => setAddMenuAnchor(undefined)}>
                <MenuItem
                  onClick={() => {
                    setAddMenuAnchor(undefined);
                    setPickerKind(FirmPickerKind.Individual);
                  }}
                >
                  A team leader
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setAddMenuAnchor(undefined);
                    setPickerKind(FirmPickerKind.Team);
                  }}
                >
                  A marketplace team
                </MenuItem>
              </Menu>
              <FormHelperText>
                {isAtLimit
                  ? `One send reaches at most ${MAXIMUM_FIRMS_PER_SEND} firms.`
                  : `Choose ${MINIMUM_FIRMS_PER_SEND} to ${MAXIMUM_FIRMS_PER_SEND} firms.`}
              </FormHelperText>
            </>
          )}

          {step === SendStep.Review && (
            <>
              <Explanation variant="body2">
                Every firm receives the same proposal.{" "}
                {responseDueBy
                  ? `Every firm answers by ${responseDueBy.asFormattedString}.`
                  : "The proposal sets no response deadline."}{" "}
                To change the terms for one firm, redline its proposal after you send.
              </Explanation>
              <List dense aria-label="Firms to send to">
                {readiness.map((entry, index) => (
                  <ListItem key={`${entry.firm.leader.userId.value}-${index}`} disableGutters>
                    <ListItemText
                      primary={entry.firm.name}
                      secondary={entry.canReceive ? "Ready to send" : entry.reason}
                      slotProps={{ secondary: { color: entry.canReceive ? "textSecondary" : "error" } }}
                    />
                  </ListItem>
                ))}
              </List>
              {sendError && <Alert severity="error">{sendError}</Alert>}
            </>
          )}

          {step === SendStep.Results && (
            <List dense aria-label="Firms sent to">
              {results.map((result) => {
                const firm = firms[result.index];
                const sent = result.outcome === "Sent";
                return (
                  <ListItem key={result.index} disableGutters>
                    <ListItemText
                      primary={firm?.name ?? `Firm ${result.index + 1}`}
                      secondary={
                        sent
                          ? "Sent"
                          : `Not sent: ${result.error ?? "the proposal could not be sent."}` +
                            (result.proposalId ? " Its draft is in your drafts." : "")
                      }
                      slotProps={{ secondary: { color: sent ? "textSecondary" : "error" } }}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          {step === SendStep.Choose && (
            <>
              <Button onClick={() => onClose(undefined)}>Cancel</Button>
              <Button variant="contained" disabled={!isSendableFirmCount(firms.length)} onClick={() => setStep(SendStep.Review)}>
                Review
              </Button>
            </>
          )}
          {step === SendStep.Review && (
            <>
              <Button disabled={isSending} onClick={() => setStep(SendStep.Choose)}>
                Back
              </Button>
              <LoadingButton
                variant="contained"
                loading={isSending}
                disabled={readyCount === 0}
                startIcon={<SendIcon />}
                onClick={handleSend}
              >
                {`Send to ${readyCount} ${readyCount === 1 ? "firm" : "firms"}`}
              </LoadingButton>
            </>
          )}
          {step === SendStep.Results && (
            <Button variant="contained" onClick={() => onClose(requestId)}>
              {requestId ? "Compare proposals" : "Done"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <ProposalFirmPicker kind={pickerKind} onClose={() => setPickerKind(undefined)} onFirmChosen={addFirm} />
    </>
  );
}
